import { BaseApiType } from "@shared/services/types";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { ApiPerFeature } from "@shared/services/endpoints/types";
import { IStorageService } from "@shared/utils/IStorageService";
import { ApiTags, AuthUser, DeepWriteable } from "@shared/types";
import { filtersReducer } from "@shared/slices/filters.slice";
import { AUTH_SLICE_PATH } from "@shared/common/constants";
import SocketHandler from "@shared/services/socket/socketHandler";
import { socketsReducer } from "@shared/slices/sockets.slice";
import { JOBS_SLICE_PATH, jobsSlice } from "@shared/slices/jobs.slice";
import { mapValues } from "lodash";
import { createApi } from "@reduxjs/toolkit/query/react";

export class SharedLib {
  state: Partial<SharedLibState> = {};
  storageService?: IStorageService = undefined;
  socketHandler?: SocketHandler;
  endpoints?: any;
  isWhop?: boolean;

  init(
    storageService: IStorageService,
    baseUrl: string,
    socketHandler: SocketHandler,
    getUserAgent?: () => Promise<string>,
    endpoints?: any,
    buildBaseQuery?: any,
    commonSlice?: any,
    createAuthSlice?: any,
    isWebView?: boolean,
    betaBaseUrl?: string,
    isWhop?: boolean,
    whopDevAuthToken?: string | null,
    whopExperienceId?: string | null
  ) {
    this.endpoints = endpoints;
    this.isWhop = isWhop;
    const baseApi = createApi({
      reducerPath: "sharedApi" as const,
      endpoints: () => ({}),
      baseQuery: buildBaseQuery?.(
        storageService,
        baseUrl,
        getUserAgent,
        isWebView,
        betaBaseUrl,
        isWhop,
        whopDevAuthToken,
        whopExperienceId
      ),
      tagTypes: Object.values(ApiTags),
      keepUnusedDataFor: 180,
    }) as any;
    const sharedApi = this.loadApi(baseApi);
    this.storageService = storageService;
    this.socketHandler = socketHandler;

    const sharedSlices = this.createSharedSlices(
      sharedApi,
      socketHandler,
      commonSlice,
      createAuthSlice
    );

    this.state = {
      sharedApi,
      baseApi,
      storageService,
      ...sharedSlices,
    };

    return { baseApi, sharedApi, ...sharedSlices };
  }

  setupStore(store: ApiStore) {
    this.state.store = store;
    this.initializeTokens();
  }

  getState(): SharedLibState {
    if (!this.state)
      throw new Error("shared library was not initialized properly.");

    return this.state as SharedLibState;
  }

  loadApi = (api?: BaseApiType): ApiPerFeature => {
    return mapValues(this.endpoints, (apiEndpoints) => {
      // @ts-ignore
      return api?.injectEndpoints(apiEndpoints);
    }) as any;
  };

  createSharedSlices(
    sharedApi: ApiPerFeature,
    socketHandler: SocketHandler,
    commonSlice: any,
    createAuthSlice: any
  ) {
    const { authSlice, authSelectors } = createAuthSlice(
      this.storageService!,
      sharedApi[AUTH_SLICE_PATH],
      socketHandler
    );

    const slices = {
      [AUTH_SLICE_PATH]: authSlice,
      common: commonSlice,
      [JOBS_SLICE_PATH]: jobsSlice,
    } as const;

    const selectors = {
      [AUTH_SLICE_PATH]: authSelectors as {
        selectUser: (state: any) => AuthUser | undefined;
        selectRealUser: (state: any) => AuthUser | undefined;
        selectToken: (state: any) => string | undefined;
        selectRefreshToken: (state: any) => string | undefined;
        selectIsLoaded: (state: any) => boolean | undefined;
      },
    } as const;

    return { slices, selectors };
  }

  getReducers() {
    if (!this.state.baseApi || !this.state.slices)
      throw new Error(
        "sharedLib is not initialized, did you forget to run sharedLib.init ?"
      );

    const sliceReducers = Object.values(this.state.slices).reduce(
      (acc, slice) => {
        acc[slice.name] = slice.reducer;

        return acc;
      },
      {} as DeepWriteable<SharedReducers>
    ) as SharedReducers;

    return {
      ...sliceReducers,
      filters: filtersReducer,
      sockets: socketsReducer,
      [this.state.baseApi.reducerPath]: this.state.baseApi.reducer,
    };
  }

  initializeTokens() {
    const authSlice = this.state?.slices?.[AUTH_SLICE_PATH];
    if (!authSlice) throw new Error("shared lib was not initialized properly");

    const token = this.storageService!.getAccessToken();
    const refreshToken = this.storageService!.getRefreshToken();
    if (token || (this.isWhop && refreshToken)) {
      this.state.store!.dispatch(
        authSlice.actions.setTokens({ token, refresh_token: refreshToken })
      );
    }
    this.state.store!.dispatch(authSlice.actions.setLoaded());
  }
}

const sharedLibInstance = new SharedLib();

// hiding methods that are not needed.
export const sharedLib = new Proxy(
  {
    init: sharedLibInstance.init.bind(sharedLibInstance),
    setupStore: sharedLibInstance.setupStore.bind(sharedLibInstance),
    getState: sharedLibInstance.getState.bind(sharedLibInstance),
    getReducers: sharedLibInstance.getReducers.bind(sharedLibInstance),
  } as ExposedSharedLib & SharedLibState,
  {
    get: (target, p) =>
      target[p as keyof ExposedSharedLib] ||
      sharedLibInstance.getState()[p as keyof SharedLibState],
  }
);

type ExposedSharedLib = Pick<
  SharedLib,
  "init" | "setupStore" | "getState" | "getReducers"
>;

type ApiStore = {
  dispatch: ThunkDispatch<any, any, any>;
  getState: () => unknown;
};

type SharedSlices = ReturnType<SharedLib["createSharedSlices"]>["slices"];

type SharedReducers = {
  [K in keyof SharedSlices]: SharedSlices[K]["reducer"];
};

export type SharedLibState = {
  sharedApi: ApiPerFeature;
  baseApi: BaseApiType;
  store: ApiStore;
  storageService: IStorageService;
} & ReturnType<SharedLib["createSharedSlices"]>;
